import { memo, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';
import { useSessionStorage } from 'usehooks-ts';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { hasRestaurantMenu } from '@/lib/restaurantMenu';
import { publicSiteUrl } from '@/lib/publicUrl';
import { gql, useQuery } from '@apollo/client';
import { awsImage, scrollTo } from '@firsttable/functions';
import { Restaurant } from '@firsttable/types';
import { Box, Container, Divider, Title } from '@firsttable/web-components/atoms';
import Text from '@firsttable/web-components/atoms/Text/Text';
import { breakpointsObject as bp } from '@firsttable/web-theme';
import { themeGet } from '@styled-system/theme-get';
import RestaurantStoreDevMenu from '@discover-components/restaurant/devMenu';
import withRestaurantStore from '@discover-data/stores/restaurant/withStore';
import Review from '@/components/organisms/Review';
import { PD7032ReviewerStatusTag } from '@/components/analytics/experiments/PD7032HighlightTopBookers';
import Button from '@/components/analytics/components/Button';
import FeatureFlagBoolean from '@/components/analytics/components/FeatureFlagBoolean';
import { TrackProvider } from '@/components/analytics/context/AnalyticsContext';
import { useModal } from '@/components/context/ModalContext';
import BookingWidgetWrapper from '@/components/organisms/BookingWidgets/BookingWidgetWrapper/BookingWidgetWrapper';
import RestaurantHero from '@/components/organisms/RestaurantHero';
import RestaurantInfo from '@/components/organisms/RestaurantInfo';
import RestaurantOthers from '@/components/organisms/RestaurantOther';
import RestaurantTitle from '@/components/organisms/RestaurantTitle';
import ReviewsLoading from '@/components/organisms/ReviewsLoading/ReviewsLoading';
import { formatTagsForAnalytics } from '@/components/analytics/functions/formatAnalytics';
import SilverstripeContext from '@/components/context/Silverstripe';
import { DiscountProvider } from '@/components/context/DiscountContext';
import NavLinkComponent from '@/components/GatsbyLinkConvertor';
import {
  RestaurantPresentationProvider,
  useRestaurantPresentationState,
  useUpdateRestaurantPresentation,
} from '@/components/context/RestaurantPresentationContext';
import useRestaurantGalleryLightBox from '@/components/hooks/useRestaurantGalleryLightBox';
import LoadingLayout from '@/components/layouts/LoadingLayout';
import SEO from '@/components/SEO';
import RestaurantReviews from '@/components/organisms/RestaurantReviews';
import { buildRestaurantSchema } from '@/lib/structuredData/restaurant';
import { serializeStructuredData } from '@/lib/structuredData/serialize';
import RestaurantMenu from './RestaurantMenu';

const BookingWidgetsLoading = () => (
  <BookingWidgetWrapper id="BookingWidgetWrapper">
    <Box className="animate-pulse">
      <Box flex={1}>
        <Box height={[12, 15]} mb={[10, 20]} maxWidth={170} bg="grayscale.300" />
        <Box height={[8, 10]} mb={[15, 25]} maxWidth={150} bg="grayscale.300" />
        <Box height={[15, 20]} maxWidth="90%" bg="grayscale.300" />
      </Box>
    </Box>
  </BookingWidgetWrapper>
);

const BookingWidgets = dynamic(
  () => import('@/components/organisms/BookingWidgets/BookingWidgets'),
  {
    ssr: false,
    loading: BookingWidgetsLoading,
  }
);

const RestaurantReviewTabs = dynamic(() => import('@/components/organisms/RestaurantReviewTabs'), {
  loading: () => <ReviewsLoading />,
});

const Sticky = styled(Box)`
  position: sticky;
  transition: all 1s ease-out;
`;

const StickyOnDesktop = styled(Box)`
  @media screen and (min-width: ${bp.m}) and (min-height: 1000px) {
    position: sticky;
    > div {
      max-height: 100vh;
      overflow: auto;
    }

    @supports (height: 100dvh) {
      > div {
        max-height: 100dvh;
      }
    }
  }
`;

const BookingWidgetsWrapper = styled(Box)`
  @media screen and (min-width: ${bp.m}) {
    ${themeGet('colorStyles.card.m')};
  }
`;

const FEATURED_REVIEW_QUERY = gql`
  query getFeaturedReview($id: Int!) {
    Review(id: $id) {
      id
      date
      dinedDate
      name
      comment
      foodRating
      serviceRating
      userAvatar
      loyaltyStatus
      favourite
      restaurant {
        id
        title
      }
    }
  }
`;

const ReviewsLazyLoadInViewContainer = memo(({ restaurant }: { restaurant: Restaurant }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    fallbackInView: true,
  });
  return (
    <Box ref={ref}>
      {inView ? <RestaurantReviewTabs restaurant={restaurant} /> : <ReviewsLoading />}
    </Box>
  );
});

ReviewsLazyLoadInViewContainer.displayName = 'Reviews';

const StickButton = ({ restaurant }) => {
  const state = useRestaurantPresentationState();
  const [openWidgets, setOpenWidgets] = useSessionStorage(`openWidgets[${restaurant.id}]`, [
    'RestaurantAvailability',
  ]);

  const stickyCtaClickHandler = () => {
    scrollTo('RestaurantAvailability');
    setOpenWidgets(['RestaurantAvailability']);
  };

  const showStickyButton =
    restaurant.offersFirstTable &&
    !state?.sampleMenu?.isOpen &&
    (state?.widgetPosition !== 'below' || !openWidgets.length);

  return (
    <Sticky
      display={[null, null, 'none']}
      zIndex={4}
      bottom={showStickyButton ? '0' : '-100%'}
      opacity={+showStickyButton}
      bg="white"
      p="s"
      boxShadow="0 0 30px rgba(0, 0, 0, 0.1)">
      <TrackProvider button_name="book a first table" button_type="cta">
        <Button
          wide
          kind="cta"
          onClick={stickyCtaClickHandler}
          subtext="Get 50% off the food bill!">
          Book a First Table
        </Button>
      </TrackProvider>
    </Sticky>
  );
};

const RestaurantBody = ({
  restaurant,
  review,
  showMenu = false,
}: {
  showMenu?: boolean;
  restaurant: Restaurant;
  review: any;
}) => {
  const setState = useUpdateRestaurantPresentation();
  const { loading, data } = useQuery(FEATURED_REVIEW_QUERY, {
    variables: { id: review },
    fetchPolicy: 'network-only',
    ssr: false,
    skip: !review,
  });

  if (review && loading) {
    return <LoadingLayout />;
  }

  if (review && data) {
    return (
      <>
        <Title mb="25px">Review</Title>
        <Review
          review={data.Review}
          nameAccessory={<PD7032ReviewerStatusTag loyaltyStatus={data.Review.loyaltyStatus} />}
        />
      </>
    );
  }

  return (
    <Box
      display={['block', 'block', 'grid']}
      gridColumnGap={48}
      gridTemplateColumns="minmax(0, 1fr) min-content"
      gridTemplateRows="min-content 1fr">
      <Box gridArea="1 / 1 / 2 / 2" minWidth={0}>
        {showMenu ? (
          <RestaurantMenu onOpenChange={(isOpen) => setState({ sampleMenu: { isOpen } })} />
        ) : (
          <RestaurantInfo restaurant={restaurant} setState={setState} />
        )}
      </Box>
      <Box gridArea="1 / 2 / 3 / 3">
        <StickyOnDesktop
          top={0}
          right={0}
          mt={[null, null, '-135px']}
          width={['100%', '100%', '450px']}>
          <BookingWidgetsWrapper>
            <BookingWidgets restaurant={restaurant} setState={setState} />
          </BookingWidgetsWrapper>
        </StickyOnDesktop>
        <Divider mt="l" mb={['l']} display={['block', 'block', 'none']} />
      </Box>
      {!showMenu && (
        <Box gridArea="2 / 1 / 3 / 2">
          <TrackProvider section="reviews">
            <RestaurantReviews restaurant={restaurant} />
            {restaurant.reviewsCount > 0 ? (
              <ReviewsLazyLoadInViewContainer restaurant={restaurant} />
            ) : (
              <Text fontStyle="italic">No reviews yet...</Text>
            )}
          </TrackProvider>
        </Box>
      )}
    </Box>
  );
};

const RestaurantEntryEffects = ({ hash }: { hash: string }) => {
  const { isModalOpen, hideModal } = useModal();

  useEffect(() => {
    // close the city page map modal if it's open (usually happens when coming from the map restaurant card link)
    if (isModalOpen) {
      hideModal();
    }

    if (hash === '#reviews') {
      scrollTo('RestaurantReviews');
    }
  }, []);

  return null;
};

const RestaurantBodyContainer = ({ children }: { children: ReactNode }) => {
  const { sampleMenu } = useRestaurantPresentationState();
  return (
    <Container
      overflow={['hidden', 'visible']}
      mb={[20, null, 40]}
      zIndex={sampleMenu?.isOpen ? 10 : 2}>
      {children}
    </Container>
  );
};

const RestaurantLayout = memo(({ hash, review }: { hash: string; review: number | false }) => {
  const {
    page: restaurant,
    global: { siteConfig },
  } = useContext(SilverstripeContext);
  const { region, parent: city } = restaurant;

  const restaurantImages = {
    allImages: {
      edges: [],
    },
  };
  restaurant.images.edges.map(({ node }) => restaurantImages.allImages.edges.push({ node }));
  const { showRestaurantLightBox } = useRestaurantGalleryLightBox(restaurantImages);

  // mainImageDescription will be something like "Enjoy Italian cuisine at Farina Pizzeria in Concord, Sydney"
  let mainImageDescription = 'Enjoy ';
  if (restaurant.cuisines.edges.length) {
    // map restaurant cuisines to string using commas and ampersand
    mainImageDescription += restaurant.cuisines.edges.map(({ node }) => node.title).join(', ');
    // replace last occurence of comma
    mainImageDescription = mainImageDescription.replace(/,(?=[^,]*$)/, ' and');
    mainImageDescription += ' cuisine at ';
  }
  mainImageDescription += `${restaurant.title} in ${restaurant.suburb?.menuTitle}`;
  if (restaurant.suburb?.menuTitle !== restaurant.region?.menuTitle) {
    mainImageDescription += `, ${restaurant.region.menuTitle}`;
  }

  const eventParams = useMemo(() => {
    const formattedCuisines = formatTagsForAnalytics(restaurant.cuisines);
    return {
      restaurant_id: restaurant.id,
      restaurant_name: restaurant.menuTitle,
      ...formattedCuisines,
      sessions_available: restaurant.availabilitySessions,
      additional_features: [],
      city_id: restaurant.cityId,
      city_name: restaurant.city,
      is_new: restaurant.isNew,
      region_ranking: restaurant.regionRanking,
      total_reviews: restaurant.reviewsCount,
      review_rating: restaurant.rating,
      restaurants: [
        {
          restaurant_id: restaurant.id,
          restaurant_name: restaurant.menuTitle,
          restaurant_country_id: restaurant.siteId,
          restaurant_region_id: restaurant.region?.id,
          restaurant_region_name: restaurant.region?.menuTitle,
          restaurant_suburb_id: restaurant.suburb?.id,
          restaurant_suburb_name: restaurant.suburb?.menuTitle,
          ...formattedCuisines,
          restaurant_rating: restaurant.rating,
          restaurant_reviews_count: restaurant.approvedReviewsCount,
        },
      ],
    };
  }, [restaurant]);

  const showMenu = restaurant.pageAction === 'menu';
  // The CMS canonical copies the URL, so /venue/lunch would point at itself. Point it at /venue.
  const isSessionPage = restaurant.pageAction === 'session';
  const pageSlug = `${restaurant.slug}${showMenu ? '/menu' : ''}`;
  const siteUrl = publicSiteUrl(siteConfig.websiteURL);
  const menuBreadcrumbs = [...(restaurant.breadcrumbs?.edges || [])];
  if (!menuBreadcrumbs.some(({ node }) => node.slug === restaurant.slug)) {
    menuBreadcrumbs.push({ node: { slug: restaurant.slug, menuTitle: restaurant.menuTitle } });
  }
  menuBreadcrumbs.push({ node: { slug: pageSlug, menuTitle: 'Menu' } });
  const restaurantJsonLd = buildRestaurantSchema(
    {
      ...restaurant,
      menuLink: hasRestaurantMenu(restaurant) ? `${restaurant.slug}/menu` : undefined,
    },
    siteConfig,
    (restaurant.images?.edges || [])
      .filter((edge) => edge?.node?.url)
      .map(({ node }) => awsImage(node.url, 1170, 655))
  );

  return (
    <RestaurantPresentationProvider>
      <RestaurantEntryEffects hash={hash} />
      <TrackProvider
        pageEventName="restaurant_viewed"
        source_page="restaurant"
        eventParams={eventParams}>
        <DiscountProvider defaultDiscount={0.5}>
          <Box as="main">
            <SEO
              id={restaurant.id}
              title={
                showMenu
                  ? `${restaurant.menuTitle} menu and prices | First Table`
                  : restaurant.metaTitleFormatted
              }
              description={
                showMenu
                  ? `View the menu for ${restaurant.menuTitle}, check dining offers and book a table.`
                  : restaurant.metaDescription
              }
              ogImage={restaurant.ogImage}
              metaTags={restaurant.metaTags}
              slug={pageSlug}
              canonical={
                (showMenu || isSessionPage) && siteUrl ? new URL(pageSlug, siteUrl).href : undefined
              }
            />
            <FeatureFlagBoolean featureFlag="102:aa_test:restaurant:web" defaultState="hidden" />
            <Head>
              <script
                type="application/ld+json"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: serializeStructuredData(restaurantJsonLd) }}
              />
            </Head>
            <Container mt={['20px', '70px']} mb={['16px', '30px']} data-testid="restaurant-page">
              <RestaurantTitle
                {...restaurant}
                NavLinkComponent={NavLinkComponent}
                breadcrumbs={showMenu ? { edges: menuBreadcrumbs } : restaurant.breadcrumbs}
                headerTitle={
                  showMenu ? `${restaurant.menuTitle} Menu and Prices` : restaurant.title
                }
                rank={restaurant.regionRanking || restaurant.cityRanking}
                reviews={restaurant.reviewsCount}
              />
            </Container>
            <Container maxWidth={1266} mb={['20px', '40px']} pl={[0, 'm']} pr={[0, 'm']}>
              <RestaurantHero
                handleRestaurantLightBox={showRestaurantLightBox}
                restaurantImages={restaurant.images.edges}
                facebookVideoURL={restaurant.facebookVideoURL}
                restaurantTitle={restaurant.title}
                mainImageDescription={mainImageDescription}
                restaurant={restaurant}
              />
            </Container>
            <RestaurantBodyContainer>
              <RestaurantBody
                showMenu={showMenu}
                review={!showMenu && review}
                restaurant={restaurant}
              />
            </RestaurantBodyContainer>
          </Box>
          <Box as="aside" data-nosnippet>
            <Container maxWidth={1200}>
              <TrackProvider section="nearby restaurants">
                <RestaurantOthers
                  NavComponent={NavLinkComponent}
                  restaurants={restaurant.regionRestaurants}
                  availabilitySession="dinner"
                  region={region}
                  city={city}
                  mt={[20, null, 100]}
                />
              </TrackProvider>
            </Container>
          </Box>
          {restaurant.status === 'Live' && <StickButton restaurant={restaurant} />}
          <RestaurantStoreDevMenu />
        </DiscountProvider>
      </TrackProvider>
    </RestaurantPresentationProvider>
  );
});
RestaurantLayout.displayName = 'RestaurantLayout';

const RestaurantRoute = () => {
  const { query } = useRouter();
  const review = query.review ? +query.review : false;
  const hash = typeof window === 'undefined' ? '' : window.location.hash;
  return <RestaurantLayout hash={hash} review={review} />;
};

export default withRestaurantStore(RestaurantRoute, 'Restaurant Page Store');
